import { useState, MouseEvent, ChangeEvent } from 'react'
import { TransformedSeriesLabels } from './types'

type DeleteMode = 'single' | 'bulk' | null

interface UseSeriesLabelManagementTableHandlersProps {
  onEditClick: (seriesLabelId: string) => void
  onDeleteSeriesLabel: (seriesLabelId: string) => void
  onDuplicateSeriesLabel: (seriesLabelId: string) => void
  onBulkDeleteSeriesLabels: (seriesLabelIds: Array<string>) => void
  seriesLabels: TransformedSeriesLabels | undefined
  canHardDelete: boolean
}

export default function useSeriesLabelManagementTableHandlers({
  onEditClick,
  onDeleteSeriesLabel,
  onDuplicateSeriesLabel,
  onBulkDeleteSeriesLabels,
  seriesLabels,
  canHardDelete,
}: UseSeriesLabelManagementTableHandlersProps) {
  const [selected, setSelected] = useState<string[]>([])
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedSeriesLabelId, setSelectedSeriesLabelId] = useState<string | null>(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [deleteMode, setDeleteMode] = useState<DeleteMode>(null)

  const handleEditClick = () => {
    if (selectedSeriesLabelId) {
      onEditClick(selectedSeriesLabelId)
      handleCloseMenu()
    }
  }

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (!canHardDelete) {
      return
    }
    if (event.target.checked) {
      setSelected(seriesLabels?.items.map((row) => row.id) ?? [])
    } else {
      setSelected([])
    }
  }
  const handleClick = (_event: MouseEvent<unknown>, id: string) => {
    if (!canHardDelete) {
      return
    }
    const selectedIndex = selected.indexOf(id)
    const newSelected = selectedIndex === -1 ? [...selected, id] : selected.filter((selectedId) => selectedId !== id)
    setSelected(newSelected)
  }

  const handleOpenMenu = (event: MouseEvent<HTMLElement>, id: string) => {
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
    setSelectedSeriesLabelId(id)
  }

  const handleCloseMenu = () => {
    setMenuAnchorEl(null)
    setSelectedSeriesLabelId(null)
  }

  const handleDeleteSeriesLabel = () => {
    if (!canHardDelete) {
      return
    }
    if (selectedSeriesLabelId) {
      setMenuAnchorEl(null)
      setDeleteMode('single')
      setConfirmDialogOpen(true)
    }
  }

  const handleBulkDeleteSeriesLabels = () => {
    if (!canHardDelete) {
      return
    }
    if (selected.length) {
      setDeleteMode('bulk')
      setConfirmDialogOpen(true)
    }
  }

  const resetDeleteConfirmation = () => {
    setConfirmDialogOpen(false)
    setDeleteMode(null)
    setSelectedSeriesLabelId(null)
    setMenuAnchorEl(null)
  }

  const handleConfirmDelete = () => {
    if (!canHardDelete) {
      return
    }
    if (deleteMode === 'single' && selectedSeriesLabelId) {
      onDeleteSeriesLabel(selectedSeriesLabelId)
    } else if (deleteMode === 'bulk' && selected.length) {
      onBulkDeleteSeriesLabels(selected)
      setSelected([])
    }
    resetDeleteConfirmation()
  }

  const handleCancelDelete = () => {
    resetDeleteConfirmation()
  }

  const handleDuplicateSeriesLabel = () => {
    if (selectedSeriesLabelId) {
      onDuplicateSeriesLabel(selectedSeriesLabelId)
      handleCloseMenu()
    }
  }

  const isPluralDelete = deleteMode === 'bulk' && selected.length > 1
  const confirmDialogTitle = isPluralDelete ? 'Delete Series Labels' : 'Delete Series Label'
  const confirmDialogDescription = isPluralDelete
    ? 'This will permanently delete the selected Series Labels and cannot be undone.'
    : 'This will permanently delete the Series Label and cannot be undone.'

  return {
    selected,
    menuAnchorEl,
    selectedSeriesLabelId,
    confirmDialogOpen,
    confirmDialogTitle,
    confirmDialogDescription,
    handleEditClick,
    handleSelectAllClick,
    handleClick,
    handleOpenMenu,
    handleCloseMenu,
    handleDeleteSeriesLabel,
    handleDuplicateSeriesLabel,
    handleBulkDeleteSeriesLabels,
    handleConfirmDelete,
    handleCancelDelete,
  }
}
